import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Modal } from "components/designV2/modal/modal";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { TextArea } from "components/designV2/textarea/textarea";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { PaymentMethod } from "model/Gig";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ReportAbsenceModalV2 } from "web-apps/company/components/common/modals/reportAbsenceModalV2.component";
import { useTimeReportDetailsPanelLogic } from "./timeReportDetailsPanel.logic";
import { ActionFooter, type ActionItem } from "./actionFooter.component";
import { CommentsSection } from "./commentsSection.component";
import { EditFooter } from "./editFooter.component";
import { ReportedTimeCard } from "./reportedTimeCard.component";
import { buildReportedTimeCardModel } from "./timeReportDetailsPanel.reportedTimeCard.mapper";
import { resolveTimeReportStatusBanner } from "./timeReportDetailsPanel.statusBanner.mapper";
import {
  Section,
  SectionTitle,
  CompactStatusBanner,
  CompactStatusBannerLabel,
  Divider,
  FeedbackBodyText,
  LoadingSpinner,
  StatusBannerLoadingWrap,
} from "./timeReportDetailsPanel.styled";

interface TimeReportDetailsPanelProps {
  timeReportId: number | null;
  onClose: () => void;
  onActionCompleted?: () => void;
}

const APPROVE_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

const DENY_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  borderColor: ColorV2.NegativeRed,
  textColor: ColorV2.NegativeRed,
  iconColor: ColorV2.NegativeRed,
  hoverBackgroundColor: "rgba(211, 78, 78, 0.12)",
  activeBackgroundColor: "rgba(211, 78, 78, 0.2)",
};

const PANEL_TITLE_ID = "time-report-details-panel-title";

export const TimeReportDetailsPanel = (
  props: TimeReportDetailsPanelProps,
): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const [actionComment, setActionComment] = useState("");

  const {
    detail,
    avatarUrl,
    isLoading,
    hasError,
    derived,
    handleClose,
    fetchDetails,
    editMode,
    actions,
    feedback,
    absenceModal,
    settings,
  } = useTimeReportDetailsPanelLogic({
    timeReportId: props.timeReportId,
    onClose: props.onClose,
    onActionCompleted: props.onActionCompleted,
  });
  const {
    canApproveAction,
    canRunActions,
    handleApprove,
    handleReject,
    handleReset,
    handleRetract,
    isActionLoading,
  } = actions;

  const onSaveEditChanges = (): void => {
    void editMode.saveEditChanges();
  };

  useEffect(() => {
    setActionComment("");
  }, [props.timeReportId]);

  const onOpenAbsenceModal = useCallback((): void => {
    absenceModal.setIsAbsenceModalOpen(true);
  }, [absenceModal]);

  const onCloseAbsenceModal = (): void => {
    absenceModal.setIsAbsenceModalOpen(false);
  };

  const onReportAbsence = (): void => {
    absenceModal.setIsAbsenceModalOpen(false);
    void fetchDetails();
    props.onActionCompleted?.();
  };

  const onCloseFeedbackModal = (): void => {
    feedback.setFeedbackState(null);
  };

  const onClosePanel = (): void => {
    handleClose();
  };

  const reportedTimeCardModel = buildReportedTimeCardModel({
    t: t,
    locale: i18n.language,
    derived,
    includeFlexTimeDeviations: settings.includeFlexTimeDeviations,
    isEditMode: editMode.isEditMode,
    isLoading,
    hasError,
    isActionLoading: actions.isActionLoading,
    editedReportedStart: editMode.editedReportedStart,
    editedReportedEnd: editMode.editedReportedEnd,
    editedBreakStart: editMode.editedBreakStart,
    editedBreakEnd: editMode.editedBreakEnd,
    canRunActions,
    deviationToleranceMinutes: settings.deviationToleranceMinutes,
  });
  const statusBannerModel = useMemo(
    () =>
      resolveTimeReportStatusBanner({
        status: derived.status,
        resolution: derived.resolution,
        translate: t,
      }),
    [derived.resolution, derived.status, t],
  );

  const hasCommentActions = useMemo(
    () =>
      derived.showRetractAction ||
      derived.showResetAction ||
      derived.showRejectAction ||
      derived.showApproveAction,
    [
      derived.showApproveAction,
      derived.showRejectAction,
      derived.showResetAction,
      derived.showRetractAction,
    ],
  );

  const actionItems = useMemo(() => {
    const isActionDisabled = !canRunActions || isActionLoading;
    const isApproveDisabled = !canApproveAction || isActionLoading;
    const trimmedComment = actionComment.trim();
    // Intentional `void`: async errors are handled in the logic layer via runAction().
    const items: ActionItem[] = [];

    if (derived.showRetractAction) {
      items.push({
        kind: "retract",
        label: t("TimeReports.Retract"),
        onClick: () => {
          void handleRetract(trimmedComment);
        },
        disabled: isActionDisabled,
        isLoading: isActionLoading,
      });
    }
    if (derived.showAbsenceAction) {
      items.push({
        kind: "absence",
        label: t("TimeReports.Absence"),
        onClick: onOpenAbsenceModal,
        disabled: isActionDisabled,
      });
    }
    if (derived.showResetAction) {
      items.push({
        kind: "reset",
        label: t("General.Reset"),
        onClick: () => {
          void handleReset(trimmedComment);
        },
        disabled: isActionDisabled,
        isLoading: isActionLoading,
      });
    }
    if (derived.showRejectAction) {
      items.push({
        kind: "reject",
        label: t("CompanyTimeReport.Reject"),
        onClick: () => {
          void handleReject(trimmedComment);
        },
        disabled: isActionDisabled,
        isLoading: isActionLoading,
      });
    }
    if (derived.showApproveAction) {
      items.push({
        kind: "approve",
        label: t("CompanyTimeReport.Approve"),
        onClick: () => {
          void handleApprove(trimmedComment);
        },
        disabled: isApproveDisabled,
        isLoading: isActionLoading,
      });
    }

    return items;
  }, [
    canApproveAction,
    canRunActions,
    handleApprove,
    handleReject,
    handleReset,
    handleRetract,
    isActionLoading,
    actionComment,
    derived.showAbsenceAction,
    derived.showApproveAction,
    derived.showRejectAction,
    derived.showResetAction,
    derived.showRetractAction,
    onOpenAbsenceModal,
    t,
  ]);

  const isPanelOpen = props.timeReportId != null;
  const shouldShowEditFooter = editMode.isEditMode;
  const shouldShowActionFooter = !editMode.isEditMode && actionItems.length > 0;
  const stickyFooter =
    shouldShowEditFooter || shouldShowActionFooter ? (
      <>
        {shouldShowEditFooter && (
          <EditFooter
            cancelLabel={t("General.Cancel")}
            saveAction={
              derived.isEditValid && derived.hasReportedTimeChanges
                ? {
                    kind: "enabled",
                    saveLabel: t("General.Save"),
                    isSaveEnabled: true,
                  }
                : {
                    kind: "disabled",
                    saveLabel: t("General.Save"),
                    isSaveEnabled: false,
                  }
            }
            isActionLoading={isActionLoading}
            onCancel={editMode.cancelEditMode}
            onSave={onSaveEditChanges}
          />
        )}
        {shouldShowActionFooter && (
          <ActionFooter
            groupAriaLabel={t("General.Options")}
            actions={actionItems}
            denyButtonStyles={DENY_BUTTON_STYLES}
            approveButtonStyles={APPROVE_BUTTON_STYLES}
          />
        )}
      </>
    ) : undefined;

  return (
    <>
      <SidePanel
        isOpen={isPanelOpen}
        onClose={onClosePanel}
        title={t("TimeReports.TimeReport")}
        titleId={PANEL_TITLE_ID}
        footer={stickyFooter}
      >
        <AvatarChip
          title={derived.workerName}
          titleIcon={
            detail?.paymentType === PaymentMethod.SIMFLEXITY_PAY ? (
              <SimflexityPayBadge
                src={SimflexityPayLogo}
                alt="SimflexityPay logo"
              />
            ) : null
          }
          subTitle={derived.workerRole}
          tags={[derived.workGroupName ?? t("TimeReports.TeamFallbackLabel")]}
          avatarUrl={avatarUrl}
        />
        {detail == null && isLoading && (
          <StatusBannerLoadingWrap>
            <LoadingSpinner />
          </StatusBannerLoadingWrap>
        )}
        {detail != null && (
          <CompactStatusBanner
            $textColor={statusBannerModel.textColor ?? ColorV2.Blue1}
            $backgroundColor={
              statusBannerModel.backgroundColor ?? ColorV2.Blue7
            }
          >
            <CustomIcon
              name={statusBannerModel.iconName}
              size="18px"
              color={statusBannerModel.textColor ?? ColorV2.Blue1}
            />
            <CompactStatusBannerLabel>
              {statusBannerModel.label}
            </CompactStatusBannerLabel>
          </CompactStatusBanner>
        )}
        <ReportedTimeCard
          model={reportedTimeCardModel}
          handlers={{
            onEnterEditMode: editMode.enterEditMode,
            onReportedStartChange: editMode.setEditedReportedStart,
            onReportedEndChange: editMode.setEditedReportedEnd,
            onBreakStartChange: editMode.setEditedBreakStart,
            onBreakEndChange: editMode.setEditedBreakEnd,
          }}
        />

        <Divider />
        <CommentsSection
          sectionTitle={t("TimeReports.Comments")}
          workerName={derived.workerName}
          companyTitle={t("ProfileCompany.Company")}
          placeholderTitle={t("TimeReports.CommentPlaceholderTitle")}
          userComment={derived.userComment}
          companyComment={derived.companyComment}
          noComments={derived.noComments}
        />
        {!editMode.isEditMode &&
          derived.hasVisibleActions &&
          hasCommentActions && (
            <Section>
              <SectionTitle>{t("Schedule.Comment")}</SectionTitle>
              <TextArea
                value={actionComment}
                onChange={setActionComment}
                placeholder={t("Schedule.Comment")}
                maxHeight={240}
                disabled={!canRunActions || isActionLoading}
              />
            </Section>
          )}
      </SidePanel>
      <ReportAbsenceModalV2
        isOpen={absenceModal.isAbsenceModalOpen}
        onClose={onCloseAbsenceModal}
        timeReportId={detail?.id ?? null}
        onReportAbsence={onReportAbsence}
      />
      <Modal
        label={
          feedback.feedbackState === "success"
            ? t("Alerts.Updated")
            : t("Alerts.OhNo")
        }
        size="compact"
        centerText
        showCloseButton={false}
        isOpen={feedback.feedbackState !== null}
        onClose={onCloseFeedbackModal}
        footer={
          <PrimaryButton
            label={t("DesignComponents.Close")}
            onClick={onCloseFeedbackModal}
          />
        }
      >
        <FeedbackBodyText>
          {feedback.feedbackState === "success"
            ? t("CompanyTimeReport.TimeReportHasBeenUpdated")
            : t("Alerts.SomethingWentWrong")}
        </FeedbackBodyText>
      </Modal>
    </>
  );
};

const SimflexityPayBadge = styled.img`
  height: 16px;
  width: auto;
  display: block;
`;
